import type { ColumnDef } from "@tanstack/react-table";
import { Badge } from "@trillion/ui/components/ui/badge";
import { createActionsColumn } from "@trillion/ui/components/admin/actions-column";
import { Lock } from "lucide-react";
import type { OAuthClient } from "@/types";

function fmt(value: string | null): string {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
}

interface OAuthClientColumnActions {
  onEdit: (c: OAuthClient) => void;
}

export function getOAuthClientColumns(
  actions: OAuthClientColumnActions
): ColumnDef<OAuthClient>[] {
  return [
    {
      accessorKey: "client_id",
      header: "Client ID",
      cell: ({ row }) => <span className="font-medium">{row.original.client_id}</span>,
    },
    {
      accessorKey: "display_name",
      header: "Name",
      cell: ({ row }) =>
        row.original.display_name ? (
          <span>{row.original.display_name}</span>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        ),
    },
    {
      accessorKey: "client_type",
      header: "Client type",
      cell: ({ row }) => <Badge variant="outline">{row.original.client_type ?? "—"}</Badge>,
    },
    {
      accessorKey: "application_type",
      header: "Application type",
      cell: ({ row }) => <Badge variant="outline">{row.original.application_type === "native" ? "Native" : "Web"}</Badge>,
    },
    {
      accessorKey: "slug",
      header: "Slug",
      cell: ({ row }) =>
        row.original.slug ? (
          <Badge variant="secondary">{row.original.slug}</Badge>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        ),
    },
    {
      accessorKey: "redirect_uris",
      header: "Redirect URIs",
      cell: ({ row }) => {
        const n = row.original.redirect_uris.length;
        return n ? (
          <span className="text-sm text-muted-foreground">
            {n} URI{n === 1 ? "" : "s"}
          </span>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        );
      },
    },
    {
      accessorKey: "secret_is_set",
      header: "Secret",
      // The secret is NEVER rendered (the API returns client_secret:null). Show a set-indicator keyed
      // off the server's secret_is_set boolean — a confidential client may have no secret yet.
      cell: ({ row }) =>
        row.original.secret_is_set ? (
          <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
            <Lock className="size-3" />
            •••• set
          </span>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        ),
    },
    {
      accessorKey: "modified_date",
      header: "Modified",
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">{fmt(row.original.modified_date)}</span>
      ),
    },
    // View/Edit only — no delete in v1.
    createActionsColumn<OAuthClient>({ onEdit: actions.onEdit }),
  ];
}
